/**
 * RuleActionsTrigger — the closing words of the sentence: what the rule DOES.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Chosen actions render as their own tinted chips (red = takes something off,
 * green = puts something back) so the consequence is legible without opening
 * the menu. Unset is a warning chip, because a rule with no action matches and
 * then silently does nothing — the one state that must not look finished.
 *
 * ⚠️ forwardRef + `...rest` are LOAD-BEARING: this renders inside Radix's
 * `<DropdownMenu.Trigger asChild>`, which clones this element to hand it the
 * open/close handlers, `aria-expanded` and its ref. A plain FC swallows all of
 * them and the menu silently never opens.
 */

import { ButtonHTMLAttributes, forwardRef } from 'react';
import { AlertCircle, ChevronDown } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { RuleActionLook } from '../rule-action-meta';

export interface RuleActionsTriggerProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  chosen: RuleActionLook[];
  summary: string;
}

export const RuleActionsTrigger = forwardRef<
  HTMLButtonElement,
  RuleActionsTriggerProps
>(({ chosen, summary, className, ...rest }, ref) => {
  const empty = chosen.length === 0;
  return (
    <button
      ref={ref}
      type="button"
      aria-label={empty ? 'Choose an action' : `Actions: ${summary}`}
      className={cn(
        'inline-flex min-h-7 flex-wrap items-center gap-1.5 rounded-md border px-2 py-1 text-[12px] font-medium transition-colors',
        empty
          ? 'border-warning/50 bg-warning/10 text-warning hover:border-warning'
          : 'border-border bg-background text-foreground hover:border-primary/50',
        className,
      )}
      {...rest}
    >
      {empty
        ? (
            <>
              <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
              Choose an action
            </>
          )
        : chosen.map((a) => (
            <span
              key={a.value}
              className={cn(
                'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-semibold',
                a.tone,
              )}
            >
              <a.Icon className="h-3 w-3 shrink-0" aria-hidden />
              {a.label}
            </span>
          ))}
      <ChevronDown
        className={cn(
          'h-3 w-3 shrink-0',
          empty ? 'text-warning/70' : 'text-muted-foreground',
        )}
        aria-hidden
      />
    </button>
  );
});
RuleActionsTrigger.displayName = 'RuleActionsTrigger';

export default RuleActionsTrigger;
